import { JSX } from "react";

import CollectionSkeleton from "@/components/cards/Collection.skeleton";
import DistributorSkeleton from "@/components/cards/Distributor.skeleton";
import MovieCardSkeleton from "@/components/cards/MovieCard/MovieCard.skeleton.tsx";
import TvCardSkeleton from "@/components/cards/TvCard/TvCard.skeleton.tsx";
import Section, { SectionProps } from "@/components/helpers/Section/Section.tsx";

import { cn } from "@/utils/cn.ts";

interface SectionSkeletonProps extends Omit<SectionProps, "children"> {
  type: "content" | "channels" | "distributor" | "collection";
}

const Content: Record<SectionSkeletonProps["type"], JSX.Element> = {
  content: (
    <div
      className={
        "grid grid-cols-6 gap-4 max-2xl:grid-cols-5 max-xl:grid-cols-4 max-[1200px]:gap-2 max-lg:grid-cols-3 max-sm:grid-cols-2"
      }
    >
      {Array.from({ length: 6 }).map((_, index) => (
        <MovieCardSkeleton
          key={index}
          className={cn("", {
            "max-2xl:hidden": index > 4,
            "max-xl:hidden": index > 3,
            "max-lg:hidden": index > 2,
            "max-sm:hidden": index > 1,
          })}
        />
      ))}
    </div>
  ),
  channels: (
    <div
      className={
        "max- max-[1200]:gap-2 max-[1600px]:grid-cols-8 grid w-full grid-cols-9 gap-4 max-2xl:grid-cols-7 max-xl:grid-cols-6 max-lg:grid-cols-5 max-md:grid-cols-4 max-sm:grid-cols-3"
      }
    >
      {Array.from({ length: 9 }).map((_, index) => (
        <TvCardSkeleton
          key={index}
          className={cn("", {
            "max-2xl:hidden": index > 6,
            "max-xl:hidden": index > 5,
            "max-lg:hidden": index > 4,
            "max-md:hidden": index > 3,
            "max-sm:hidden": index > 2,
          })}
        />
      ))}
    </div>
  ),
  distributor: (
    <div className={"grid grid-cols-3 gap-4 max-lg:grid-cols-2 max-md:grid-cols-1"}>
      {Array.from({ length: 3 }).map((_, index) => (
        <DistributorSkeleton
          key={index}
          className={cn("", {
            "max-lg:hidden": index > 1,
            "max-md:hidden": index > 0,
          })}
        />
      ))}
    </div>
  ),
  collection: (
    <div
      className={
        "max-[1200]:gap-2 max-[1440px]:grid-cols-5 max-[1280px]:grid-cols-4 max-[1024px]:grid-cols-3 max-[640px]:grid-cols-2 grid grid-cols-6 gap-4"
      }
    >
      {Array.from({ length: 6 }).map((_, index) => (
        <CollectionSkeleton
          key={index}
          className={cn("", {
            "max-[1440px]:hidden": index > 4,
            "max-[1280px]:hidden": index > 3,
            "max-[1024px]:hidden": index > 2,
            "max-[640px]:hidden": index > 1,
          })}
        />
      ))}
    </div>
  ),
};

const SectionSkeleton = ({ type, ...otherProps }: SectionSkeletonProps): JSX.Element => {
  return (
    <Section className={type === "distributor" ? "pt-0" : ""} {...otherProps}>
      {Content[type]}
    </Section>
  );
};

export default SectionSkeleton;
